import React, { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as ajaxEndpoints from "../../api/ajax-endpoints";

import { TBusinessInfo } from "../../custom-hooks/useProspectivePrincipals";
import { useForm } from "react-hook-form";

import CustomInputField from "../CustomHTMLElements/CustomInputField";
import { postData } from "../../newApis/apiMethods";
import { message } from "antd";
import { errorHandler } from "../../helpers/errorHandler";
import { appInsights } from "../../config/appInsights";
import CustomSelectDropdown from "../CustomHTMLElements/CustomSelectDropdown";
import useGetBusinessTypes from "../../custom-hooks/useGetBusinessTypes";
interface IapproveProps {
  dataRecord: {
    action: string;
    currentProspectivePrincipalData: TBusinessInfo;
  };
  onCloseModal: () => void;
}
interface IprincipalProps {
  rcNumber: string;
  businessName: string;
}
const schema = yup.object().shape({
  rcNumber: yup.string().required("Please enter RC Number"),
  businessName: yup
    .string()
    .required(
      "Please ensure RC number is inputted for Business Name Validation",
    ),
  business_type: yup.string().trim().required("Please select a business type"),
});

const ProspectivePrincipalApprovalModal = (props: IapproveProps) => {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isNotifyCustomerVerifying, setIsNotifyCustomerVerifying] =
    useState(false);
  const [isApprovalSuccessful, setIsApprovalSuccessful] = useState(false);
  const [isLoadingApproval, setIsLoadingApproval] = useState(false);
  const {
    dataRecord: { currentProspectivePrincipalData, refetch },
    onCloseModal,
  } = props;

  const { handleSubmit, register, watch, setValue, errors } = useForm({
    resolver: yupResolver(schema),
  });
  const currentBusinessName = watch("businessName");
  const currentBusinessType = watch("business_type");
  const currentRcNumber = watch("rcNumber");
  const { data: businessTypes, isLoading: isLoadingBusinessTypes } =
    useGetBusinessTypes({
      sourceTypeId: "1",
    });
  const handleApprovePrincipal = async (data: IprincipalProps) => {
    setIsLoadingApproval(true);
    const reqBody = {
      rcNumber: data?.rcNumber,
      businessName: currentBusinessName ? currentBusinessName : "",
      customerSubTypeId: currentBusinessType
        ? Number(currentBusinessType)
        : null,
      registrationDate: currentProspectivePrincipalData?.registrationDate,
      state: currentProspectivePrincipalData?.state,
      city: currentProspectivePrincipalData?.city,
      address: currentProspectivePrincipalData?.address,
    };
    try {
      const response = await postData(
        `${ajaxEndpoints.PROSPECT_PRINCIPALS}/${currentProspectivePrincipalData?.id}/approve`,
        reqBody,
      );
      if (response?.status_code === 200) {
        refetch();
        message.success(response?.message);
        setTimeout(() => {
          onCloseModal();
        }, 3000);
        setIsApprovalSuccessful(true);
      }
    } catch (error) {
      message.error(errorHandler(error));
      appInsights.trackException({
        exception: error as Error,
        properties: {
          fileName: "ProspectivePrincipalApprovalModal.tsx",
          errorMessage: errorHandler(error),
        },
      });
    } finally {
      setIsLoadingApproval(false);
    }
  };
  const handleBusinessRegistrationNumberValidation = async () => {
    setIsVerifying(true);
    const reqBody = {
      businessRegistrationNumber:
        currentRcNumber || currentProspectivePrincipalData?.rcNumber,
      countryCode: "NG",
      businessType: Number(currentBusinessType), //addition of Business type to verify Business number accurately
    };
    try {
      const response = await postData(
        `${ajaxEndpoints.BUSINESS_NUMBER_VALIDATION}`,
        reqBody,
      );
      if (response?.status_code === 200) {
        message.success("Business name verified successfully ");
        setValue("businessName", response?.data?.fullName);
      }
    } catch (error) {
      message.error(errorHandler(error));
      appInsights.trackException({
        exception: error as Error,
        properties: {
          fileName: "ProspectivePrincipalApprovalModal.tsx",
          customerBusinessRegistrationNumber:
            currentProspectivePrincipalData?.rcNumber,
          errorMessage: errorHandler(error),
        },
      });
    } finally {
      setIsVerifying(false);
    }
  };
  const handleNotifyCustomers = async () => {
    setIsNotifyCustomerVerifying(true);

    try {
      const response = await postData(
        `${ajaxEndpoints.PROSPECT_PRINCIPALS}/${currentProspectivePrincipalData?.id}/notify`,
      );
      if (response?.status_code === 200) {
        message.success(response?.message);
      }
      setTimeout(() => {
        setIsApprovalSuccessful(false);
      }, 60000);
    } catch (error) {
      message.error(errorHandler(error));
      appInsights.trackException({
        exception: error as Error,
        properties: {
          fileName: "ProspectivePrincipalApprovalModal.tsx",
          errorMessage: errorHandler(error),
        },
      });
    } finally {
      setIsNotifyCustomerVerifying(false);
    }
  };
  useEffect(() => {
    let debounceVerifyBusiness: NodeJS.Timeout = setTimeout(() => {});

    if (currentProspectivePrincipalData?.rcNumber && !currentRcNumber) {
      setValue("rcNumber", currentProspectivePrincipalData?.rcNumber);
      if (currentBusinessType) {
        debounceVerifyBusiness = setTimeout(() => {
          handleBusinessRegistrationNumberValidation();
        }, 3000);
      }
    } else {
      if (currentBusinessType) {
        debounceVerifyBusiness = setTimeout(() => {
          handleBusinessRegistrationNumberValidation();
        }, 3000);
      }
    }

    return () => clearTimeout(debounceVerifyBusiness);
  }, [
    currentProspectivePrincipalData?.rcNumber,
    currentRcNumber,
    currentBusinessType,
  ]);

  return (
    <div
      className="Modal__dialog lg-modal md-modal animated slideInDown"
      role="document"
    >
      <div
        className="LoanModal Modal__content"
        style={{
          overflow: "visible",
          maxWidth: "43.625rem",
          height: "auto",
          margin: "auto",
        }}
      >
        <div className="Modal__body">
          <h2 className={`Modal__title mb-5 font-weight-bold`}>
            Approve Principal
          </h2>
          <form onSubmit={handleSubmit(handleApprovePrincipal)}>
            <div className="row">
              <div className="col-12">
                <CustomSelectDropdown
                  label="Business Type"
                  name="business_type"
                  reference={register}
                  errors={errors.business_type}
                  isShowLabel={true}
                >
                  <option value="">Select Business type</option>
                  {businessTypes &&
                    businessTypes?.map((_businessType: any) => (
                      <option key={_businessType?.id} value={_businessType?.id}>
                        {_businessType?.name}
                      </option>
                    ))}
                </CustomSelectDropdown>
                {isLoadingBusinessTypes && (
                  <small>Business Types Loading...</small>
                )}
              </div>
              <div className="col-12">
                <CustomInputField
                  type="number"
                  name="rcNumber"
                  reference={register}
                  maxLength={12}
                  label="RC Number"
                  placeholder="Please Enter RC Number"
                  errors={errors.rcNumber}
                />
              </div>

              <div className="col-12">
                <CustomInputField
                  type="text"
                  name="businessName"
                  reference={register}
                  label="Business Name"
                  errors={errors.businessName}
                  readOnly
                  loadingMessage={
                    isVerifying ? "Verifying Business Name..." : ""
                  }
                />
              </div>
            </div>

            <div className="d-flex justify-content-between">
              <button
                type="submit"
                className="btn advancly-btn"
                disabled={isLoadingApproval || !currentBusinessName}
              >
                {isLoadingApproval ? "Loading..." : "Approve Principal"}
              </button>
              {/* <button
                type="button"
                className="btn advancly-btn"
                disabled={isNotifyCustomerVerifying || !isApprovalSuccessful}
                onClick={handleNotifyCustomers}
              >
                {isLoadingApproval ? "Loading..." : "Notify Customer"}
              </button> */}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProspectivePrincipalApprovalModal;
